import { c as _c } from "react/compiler-runtime";
import { ClothesLine } from '@/components/Organisms/ClothesLine/ClothesLine';
import { AnalystReactionsPageAnalyticsInfo } from '@/components/Pages/AnalystReactions/AnalystReactionViewPage';
import { InsightPageAnalyticsInfo } from '@/components/Pages/Insights/InsightExecSumPage';
import { NewsPageAnalyticsInfo } from '@/components/Pages/News/NewsRsfPage';
import { ShortsPageAnalyticsInfo } from '@/components/Pages/Shorts/ShortsViewPage';
import { ThemePageAnalyticsInfo } from '@/components/Pages/Themes/ThemePage';
import { ItemPageRenderer } from '@/components/Templates/InfiniteScrollPage/ItemPageRenderer';
import { SEARCH_CONTEXT_STORAGE_KEY } from '@/contexts/SearchContext';
import { useDebouncedEffect } from '@/hooks/useDebouncedEffect';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { useMounted } from '@/hooks/useMounted';
import { SailthruTracking } from '@/hooks/useSailthruTracking';
import { trackView } from '@/hooks/useUserContentViewTracker';
import type { IContentItem } from '@/interfaces';
import { ContentType } from '@/interfaces';
import { refreshBBAnalyticsProviders, trackEvent, trackPageLoad } from '@/services/Analytics/BBAnalytics';
import type { IBaseAnalyticsData } from '@/services/Analytics/bnefAnalyticsInterfaces';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import { BrowserHttpClient } from '@/services/Http/BrowserHttpClient';
import SessionStorage from '@/services/SessionStorage';
import { useRouter } from 'next/router';
import type { RefObject } from 'react';
import { createRef, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
export interface IInfiniteScrollProps<T> {
  /** Current item used to combine with search results */
  currentItem: IContentItem;
  /** This will be provided back to the `PageRenderer` as the `initialData` argument */
  initialPageData: T;
  /** Fetch more IContentItem's from search results. Pass initialData for dedupe */
  resultsBuilder: (initialItem: IContentItem, query?: Record<string, any>) => Promise<IContentItem[]>;
}
export interface IResultListItem {
  ref: RefObject<HTMLDivElement>;
  item: IContentItem;
}
export enum PresetQuery {
  Popular = 'popular',
  Recommended = 'recommendations',
  NewsRecommended = 'news-recommendations',
  NewsFeatured = 'news-featured',
}

// Preset API calls are stored in code rather than the URL, to prevent the User from tampering with them
const presetUrls = {
  [PresetQuery.Popular]: '/popular',
  [PresetQuery.Recommended]: '/recommendations',
  [PresetQuery.NewsRecommended]: '/recommendations/news',
  [PresetQuery.NewsFeatured]: '/news_stories/featured'
};
export const usePresetContextQuery = preset => {
  const $ = _c(3);
  let t0;
  if ($[0] !== preset) {
    t0 = () => {
      SessionStorage.set(SEARCH_CONTEXT_STORAGE_KEY, preset);
    };
    $[0] = preset;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useEffect(t0, t1);
};
const getPresetQueryBuilder = (path: string) => {
  return async (initialItem: IContentItem, _?: Record<string, any>): Promise<IContentItem[]> => {
    const searchResults = await new BrowserHttpClient().get(path);
    return searchResults?.items.filter((item: IContentItem) => {
      return item.id.toString() !== initialItem.id;
    }) || [];
  };
};

// attach refs and loading state to each item
const resultToList = (resultItems: IContentItem[]): IResultListItem[] => {
  return resultItems.map((v: IContentItem) => {
    return {
      ref: createRef<HTMLDivElement>(),
      item: v
    };
  });
};
const getAnalyticsInfoObject = (type: ContentType) => {
  switch (type) {
    case ContentType.insight:
      return InsightPageAnalyticsInfo;
    case ContentType.short:
      return ShortsPageAnalyticsInfo;
    case ContentType.analystreaction:
      return AnalystReactionsPageAnalyticsInfo;
    case ContentType.news:
      return NewsPageAnalyticsInfo;
    case ContentType.theme:
      return ThemePageAnalyticsInfo;
  }
};
export const getBaseAnalytics = (type: ContentType): IBaseAnalyticsData => {
  const typeData = getAnalyticsInfoObject(type);
  if (!typeData) {
    return {};
  }
  return {
    page: {
      pageInfo: {
        pageName: typeData.pageName
      },
      category: {
        siteSection: typeData.siteSection
      }
    }
  };
};
// This function ensures all pages have the same BB Analytics shape
// And when infinite scroll change route on client-side, all props are properly updated
export const getAnalyticsData = (type: ContentType, itemId: IContentItem['id']): IBaseAnalyticsData => {
  const typeData = getAnalyticsInfoObject(type);
  if (!typeData) {
    return {};
  }
  const data = getBaseAnalytics(type);
  data.page.content = {
    type: typeData.type,
    id: `${typeData.type}-${itemId}`
  };
  return data;
};
export const InfiniteScrollPage = t0 => {
  const $ = _c(56);
  const {
    resultsBuilder,
    initialPageData,
    currentItem
  } = t0;
  let t1;
  if ($[0] !== currentItem) {
    t1 = () => resultToList([currentItem]);
    $[0] = currentItem;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [resultsList, setResultsList] = useState(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [searchResults, setSearchResults] = useState(t2);
  const [activePosition, setActivePosition] = useState(0);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [0];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [loadedPositions, setLoadedPositions] = useState(t3);
  const [progress, setProgress] = useState();
  const containerRef = useRef(null);
  const router = useRouter();
  const isMounted = useMounted();
  let t4;
  if ($[4] !== currentItem || $[5] !== isMounted || $[6] !== resultsBuilder || $[7] !== router) {
    t4 = () => {
      let builder = resultsBuilder;
      const {
        context,
        ...restQuery
      } = router.query;
      if (context && typeof context === "string") {
        SessionStorage.set(SEARCH_CONTEXT_STORAGE_KEY, context);
        router.replace({
          pathname: router.pathname,
          query: {
            ...restQuery
          }
        }, undefined, {
          shallow: true
        });
      }
      const q = SessionStorage.get(SEARCH_CONTEXT_STORAGE_KEY);
      if (presetUrls[q as PresetQuery]) {
        builder = getPresetQueryBuilder(presetUrls[q as PresetQuery]);
      }
      builder(currentItem, q).then(v => {
        if (isMounted()) {
          setSearchResults(v);
        }
      }).catch(_temp);
    };
    $[4] = currentItem;
    $[5] = isMounted;
    $[6] = resultsBuilder;
    $[7] = router;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[10] !== currentItem || $[11] !== searchResults) {
    t6 = () => {
      setResultsList(resultToList([currentItem, ...searchResults]));
    };
    t7 = [currentItem, searchResults];
    $[10] = currentItem;
    $[11] = searchResults;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  if ($[14] !== activePosition || $[15] !== loadedPositions || $[16] !== resultsList) {
    t8 = () => {
      if (!loadedPositions.includes(activePosition) || !containerRef.current || !resultsList[activePosition]?.ref.current) {
        return;
      }
      const startingOffset = activePosition === 0 ? containerRef.current.getBoundingClientRect().top + window.scrollY : containerRef.current.getBoundingClientRect().top + window.scrollY + 16;
      const rect = resultsList[activePosition].ref.current.getBoundingClientRect();
      let currentProgress = (startingOffset - rect.top) / rect.height * 100;
      if (rect.bottom < window.innerHeight && activePosition === loadedPositions.length - 1) {
        currentProgress = 100;
      }
      if (currentProgress >= 25) {
        setLoadedPositions(currentPositions => {
          if (!currentPositions.includes(activePosition + 1)) {
            return [...currentPositions, activePosition + 1];
          }
          return currentPositions;
        });
      }
      if (currentProgress < 0) {
        if (resultsList[activePosition - 1]?.ref?.current) {
          setActivePosition(activePosition - 1);
          setProgress(100);
          return;
        }
      }
      if (currentProgress > 100 && loadedPositions.includes(activePosition + 1)) {
        setActivePosition(activePosition + 1);
        setProgress(0);
        return;
      }
      setProgress(currentProgress);
    };
    $[14] = activePosition;
    $[15] = loadedPositions;
    $[16] = resultsList;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleScroll = t8;
  let t9;
  if ($[18] !== loadedPositions || $[19] !== resultsList) {
    t9 = newPosition => {
      if (!loadedPositions.includes(newPosition)) {
        const item = resultsList[newPosition]?.item;
        if (item) {
          trackView(item.id, item.type as ContentType, null);
        }
      }
      setLoadedPositions([newPosition]);
      setActivePosition(newPosition);
      setProgress(0);
      if ("scrollTo" in window) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth"
        });
      } else {
        document.documentElement.scrollTop = 0;
      }
    };
    $[18] = loadedPositions;
    $[19] = resultsList;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const onJumpPosition = t9;
  const isFirstLoad = useRef(true);
  let t10;
  if ($[21] !== activePosition || $[22] !== resultsList || $[23] !== router) {
    t10 = () => {
      if (!resultsList[activePosition]) {
        return;
      }
      const newItem = resultsList[activePosition].item;
      const handleRouteChange = () => {
        document.title = newItem.title;
        refreshBBAnalyticsProviders(getAnalyticsData(newItem.type, newItem.id));
        trackPageLoad();
        trackEvent(TrackingAction.Scroll, TrackingCategory.BnefContent, null, activePosition);
      };
      router.events.on("routeChangeComplete", handleRouteChange);
      if (isFirstLoad.current) {
        isFirstLoad.current = false;
        if (!router.query?.context) {
          SailthruTracking.trackSavedTags(newItem.id, newItem.type);
        }
      } else {
        router.replace({
          pathname: router.pathname,
          query: {
            id: newItem.id,
            ...router.query
          }
        }, {
          pathname: newItem.url
        }, {
          shallow: true
        });
        SailthruTracking.trackSavedTags(newItem.id, newItem.type);
      }
      return () => {
        router.events.off("routeChangeComplete", handleRouteChange);
      };
    };
    $[21] = activePosition;
    $[22] = resultsList;
    $[23] = router;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== activePosition) {
    t11 = [activePosition];
    $[25] = activePosition;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  useEffect(t10, t11);
  let t12;
  if ($[27] !== handleScroll) {
    t12 = () => {
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    };
    $[27] = handleScroll;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== activePosition || $[30] !== loadedPositions || $[31] !== resultsList) {
    t13 = [activePosition, loadedPositions, resultsList];
    $[29] = activePosition;
    $[30] = loadedPositions;
    $[31] = resultsList;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  useEffect(t12, t13);
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [];
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  useEffect(_temp2, t14);
  const [renderClothesLine, setRenderClothesLine] = useState(false);
  let t15;
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      setRenderClothesLine(true);
    };
    t16 = [];
    $[34] = t15;
    $[35] = t16;
  } else {
    t15 = $[34];
    t16 = $[35];
  }
  useEffect(t15, t16);
  let t17;
  if ($[36] !== activePosition || $[37] !== onJumpPosition || $[38] !== progress || $[39] !== renderClothesLine || $[40] !== resultsList) {
    t17 = renderClothesLine && <ClothesLine searchResults={resultsList} activePosition={activePosition} onChangePosition={onJumpPosition} progress={progress} />;
    $[36] = activePosition;
    $[37] = onJumpPosition;
    $[38] = progress;
    $[39] = renderClothesLine;
    $[40] = resultsList;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== activePosition || $[43] !== initialPageData || $[44] !== loadedPositions || $[45] !== resultsList) {
    let t19;
    if ($[47] !== activePosition || $[48] !== initialPageData || $[49] !== loadedPositions) {
      t19 = (v_0, i) => {
        if (loadedPositions.includes(i)) {
          return <StyledPage ref={v_0.ref} key={v_0.item.url} $isActive={activePosition === i}><ItemPageRenderer id={v_0.item.id} expectedType={v_0.item.type} initialData={i === 0 ? initialPageData : undefined} isActive={activePosition === i} /><EndOfArticleTrigger callback={() => setLoadedPositions(currentPositions_0 => {
              if (!currentPositions_0.includes(i + 1)) {
                return [...currentPositions_0, i + 1];
              }
              return currentPositions_0;
            })} /></StyledPage>;
        }
      };
      $[47] = activePosition;
      $[48] = initialPageData;
      $[49] = loadedPositions;
      $[50] = t19;
    } else {
      t19 = $[50];
    }
    t18 = resultsList.map(t19);
    $[42] = activePosition;
    $[43] = initialPageData;
    $[44] = loadedPositions;
    $[45] = resultsList;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[51] !== t18) {
    t19 = <div ref={containerRef}>{t18}</div>;
    $[51] = t18;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== t17 || $[54] !== t19) {
    t20 = <>{t17}{t19}</>;
    $[53] = t17;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  return t20;
};
const StyledPage = styled.article<{
  $isActive: boolean;
}>`
    min-height: 60vh;
    margin-bottom: var(--size-grid-unit);
    padding-bottom: var(--size-grid-unit);
    border-bottom: solid 1px var(--color-lines-strongDivider);

    break-after: page;
    @media print {
        ${({
  $isActive
}) => !$isActive && css`
                display: none;
            `};
        &:first-child {
            padding-top: 0;
        }
    }
`;
const EndOfArticleTrigger = t0 => {
  const $ = _c(10);
  const {
    callback
  } = t0;
  const ref = useRef(null);
  const [ratio, setRatio] = useState(0);
  const [triggerActive, setTriggerActive] = useState(false);
  useIntersectionObserver(ref, setRatio, 2);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setTriggerActive(true);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useDebouncedEffect(t1, 3000, t2);
  let t3;
  if ($[2] !== callback || $[3] !== ratio || $[4] !== triggerActive) {
    t3 = () => {
      if (triggerActive && ratio > 0) {
        callback();
      }
    };
    $[2] = callback;
    $[3] = ratio;
    $[4] = triggerActive;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== ratio || $[7] !== triggerActive) {
    t4 = [ratio, triggerActive];
    $[6] = ratio;
    $[7] = triggerActive;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ZeroHeightDiv ref={ref} />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
const ZeroHeightDiv = styled.div`
    width: 0;
    height: 0;
`;
function _temp() {}
function _temp2() {
  document.querySelector("footer")?.setAttribute("style", "display: none !important");
}